// components/artifacts/renderers/LinkArtifactRenderer.tsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { LinkArtifact, LinkData } from "@/types/Artifact";
import { ExternalLink, Globe, Loader2, RefreshCw, Maximize2, Minimize2, ImageOff } from "lucide-react";

// Proxy URLs - use frontend proxy which forwards to backend
const getProxyBaseUrl = (): string => {
  // Always use the current frontend origin for the proxy
  return window.location.origin;
};

const getProxyUrl = (targetUrl: string): string => {
  return `${getProxyBaseUrl()}/proxy/web/proxy?url=${encodeURIComponent(targetUrl)}`;
};

const getMetadataUrl = (targetUrl: string): string => {
  return `${getProxyBaseUrl()}/proxy/web/metadata?url=${encodeURIComponent(targetUrl)}`;
};

interface LinkArtifactRendererProps {
  artifact: LinkArtifact;
}

interface LinkMetadata {
  url: string;
  domain: string;
  title: string | null;
  description: string | null;
  image: string | null;
  favicon: string | null;
  siteName?: string | null;
  type?: string;
  error?: boolean;
}

const parseLinkData = (content: LinkData | string): LinkData | null => {
  if (typeof content === "string") {
    try {
      return JSON.parse(content);
    } catch {
      if (content.startsWith("http://") || content.startsWith("https://")) {
        return { url: content };
      }
      return null;
    }
  }
  return content;
};

const extractDomain = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const getFaviconUrl = (url: string, customFavicon?: string): string => {
  if (customFavicon) return customFavicon;
  try {
    const urlObj = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`;
  } catch {
    return "";
  }
};

// Preview Card Component for when iframe fails
const PreviewCard: React.FC<{ 
  metadata: LinkMetadata; 
  onOpenExternal: () => void;
  onRetry: () => void;
}> = ({ metadata, onOpenExternal, onRetry }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div className="flex h-full flex-col items-center justify-center bg-gradient-to-br from-muted/30 via-background to-muted/20 p-8">
      <div className="w-full max-w-lg overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg transition-all hover:shadow-xl">
        {/* Preview Image */}
        {metadata.image && !imageError ? (
          <div className="relative h-48 w-full overflow-hidden bg-muted">
            <img
              src={metadata.image}
              alt={metadata.title || "Preview"}
              className="h-full w-full object-cover"
              onError={() => setImageError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        ) : (
          <div className="flex h-32 w-full items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10">
            {imageError ? (
              <ImageOff className="h-12 w-12 text-muted-foreground/40" />
            ) : (
              <Globe className="h-12 w-12 text-primary/40" />
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          {/* Site info */}
          <div className="mb-3 flex items-center gap-2">
            {metadata.favicon && (
              <img
                src={metadata.favicon}
                alt=""
                className="h-4 w-4"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            <span className="text-xs font-medium text-muted-foreground">
              {metadata.siteName || metadata.domain}
            </span>
          </div>

          {/* Title */}
          <h3 className="mb-2 text-lg font-semibold leading-tight text-foreground line-clamp-2">
            {metadata.title || metadata.domain}
          </h3>

          {/* Description */}
          {metadata.description && (
            <p className="mb-4 text-sm text-muted-foreground line-clamp-3">
              {metadata.description}
            </p>
          )}

          {/* URL */}
          <p className="mb-4 truncate text-xs font-mono text-muted-foreground/70">
            {metadata.url}
          </p>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={onOpenExternal}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <ExternalLink className="h-4 w-4" />
              Im Browser öffnen
            </button>
            <button
              onClick={onRetry}
              className="flex items-center justify-center gap-2 rounded-lg bg-muted px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/80"
              title="Erneut versuchen"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Info text */}
      <p className="mt-4 max-w-md text-center text-xs text-muted-foreground">
        Diese Seite kann nicht eingebettet werden. Klicke auf den Button um sie im Browser zu öffnen.
      </p>
    </div>
  );
};

export const LinkArtifactRenderer: React.FC<LinkArtifactRendererProps> = React.memo(({ 
  artifact 
}) => {
  const linkData = useMemo(() => parseLinkData(artifact.content), [artifact.content]);

  const url = linkData?.url || "";
  const favicon = linkData?.favicon;
  const domain = extractDomain(url);
  const faviconUrl = getFaviconUrl(url, favicon);
  
  // Check if URL is a PDF or other non-HTML content
  const isPdfUrl = useMemo(() => {
    return url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('pdf');
  }, [url]);

  const [isLoading, setIsLoading] = useState(true);
  const [showPreviewCard, setShowPreviewCard] = useState(false);
  const [metadata, setMetadata] = useState<LinkMetadata | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [key, setKey] = useState(0);
  const [useProxy, setUseProxy] = useState(!isPdfUrl); // Don't proxy PDFs initially
  const [loadAttempts, setLoadAttempts] = useState(0);

  const iframeSrc = useMemo(() => {
    // Don't use proxy for PDFs - they won't work through the HTML proxy
    if (isPdfUrl) {
      return url;
    }
    return useProxy ? getProxyUrl(url) : url;
  }, [url, useProxy, isPdfUrl]);

  // Fetch metadata when showing preview card
  useEffect(() => {
    if (showPreviewCard && !metadata && url) {
      fetch(getMetadataUrl(url))
        .then(res => res.json())
        .then(data => setMetadata(data))
        .catch(() => {
          // Fallback metadata
          setMetadata({
            url,
            domain,
            title: domain,
            description: null,
            image: null,
            favicon: faviconUrl,
          });
        });
    }
  }, [showPreviewCard, metadata, url, domain, faviconUrl]);

  const handleOpenExternal = () => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setShowPreviewCard(false);
    setMetadata(null);
    // Reset to not using proxy for PDFs, otherwise use proxy
    setUseProxy(!isPdfUrl);
    setLoadAttempts(0);
    setKey((k) => k + 1);
  };

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    // Reset attempts on success
    setLoadAttempts(0);
  }, []);

  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setLoadAttempts(prev => prev + 1);
    
    // For PDFs, skip proxy attempt and go straight to preview
    if (isPdfUrl) {
      setShowPreviewCard(true);
      return;
    }
    
    // If proxy failed and we haven't tried direct yet
    if (useProxy && loadAttempts < 1) {
      setUseProxy(false);
      setIsLoading(true);
      setKey((k) => k + 1);
    } else {
      // All attempts failed - show preview card
      setShowPreviewCard(true);
    }
  }, [useProxy, loadAttempts, isPdfUrl]);

  // Check for iframe load issues via timeout
  useEffect(() => {
    if (isLoading && !showPreviewCard) {
      const timeout = setTimeout(() => {
        // If still loading after 10 seconds, assume failure
        if (isLoading) {
          handleIframeError();
        }
      }, 10000); // Reduced from 25s to 10s
      return () => clearTimeout(timeout);
    }
  }, [isLoading, showPreviewCard, key, handleIframeError]);

  if (!linkData || !linkData.url) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center rounded-lg bg-muted/30 p-4">
        <div className="text-center text-muted-foreground">
          <Globe className="mx-auto mb-2 h-8 w-8 opacity-50" />
          <p>Invalid link data format</p>
          <p className="mt-1 text-xs">Expected: {"{ url: string }"}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`flex flex-col bg-background ${isFullscreen ? "fixed inset-0 z-50" : "h-full"}`} 
      style={{ minHeight: isFullscreen ? "100vh" : "500px" }}
    >
      {/* Browser-like Header */}
      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-3 py-2">
        <div className="flex items-center gap-2 rounded-md bg-background/80 px-3 py-1.5 flex-1 min-w-0">
          {faviconUrl && (
            <img
              src={faviconUrl}
              alt=""
              className="h-4 w-4 shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <span className="truncate text-xs text-muted-foreground font-mono">
            {url}
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleRefresh}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Neu laden"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title={isFullscreen ? "Verkleinern" : "Vollbild"}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={handleOpenExternal}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Im Browser öffnen"
          >
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 bg-white dark:bg-background">
        {/* Loading Overlay */}
        {isLoading && !showPreviewCard && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Seite wird geladen...</span>
            </div>
          </div>
        )}

        {/* Preview Card (when iframe fails) */}
        {showPreviewCard && metadata && (
          <PreviewCard 
            metadata={metadata} 
            onOpenExternal={handleOpenExternal}
            onRetry={handleRefresh}
          />
        )}

        {/* Loading metadata for preview */}
        {showPreviewCard && !metadata && (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {/* iFrame - hidden when showing preview card */}
        {!showPreviewCard && (
          <iframe
            key={key}
            src={iframeSrc}
            className="h-full w-full border-0"
            style={{ minHeight: isFullscreen ? "calc(100vh - 48px)" : "452px" }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer-when-downgrade"
            title={artifact.title || domain}
          />
        )}
      </div>
    </div>
  );
});

LinkArtifactRenderer.displayName = "LinkArtifactRenderer";
